import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { ContentEditor } from '../ui/ContentEditor';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { CheckList } from '../ui/CheckList';
import { ViewOnSite } from './ViewOnSite';
import { RoutineMarginPanel } from './RoutineMarginPanel';
import { useEditorItem } from './useEditorItem';
import { deleteBlockedReason, routineLinks } from './catalogLinks';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { routineSeoChecks } from './catalogSeo';
import { routineSeoAuto } from '../../utils/seoAuto';
import { nextTierIn, tierIn } from '../../data/gifts';
import { skinTypes } from '../../data/concerns';
import { emptyRoutine, oneOf, routineChecks, routineLevels } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
const back = {
  to: '/be/protocollen',
  label: 'Alle protocollen'
};
const levelLabel: Record<(typeof routineLevels)[number], string> = {
  Start: 'Startprotocol (3–4 producten)',
  Compleet: 'Compleet protocol'
};

/** "Wat erbij zit" komt als lijst uit de ContentEditor; alleen tekstregels tellen. */
const asLines = (value: unknown): string[] => Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** Eén protocol: verhaal, producten in volgorde, wat erbij zit en de prijs (automatisch). */
export function RoutineEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: routine,
    set
  } = useEditorItem('routines', id, emptyRoutine);
  if (!routine) return <MissingItem title="Protocol niet gevonden" back={back} data-id="element-865" data-mp="3vzt4jr8ux7ds" />;
  const totals = index.routineTotals(routine);
  // De staffel uit het concept: een wijziging in Instellingen is hier meteen te zien.
  const gift = tierIn(site.giftTiers, totals.price);
  const next = nextTierIn(site.giftTiers, totals.price);
  const covered = skinTypes.filter(s => totals.items.some(p => p.skinTypes.includes(s)));
  const usedBy = routineLinks(catalog, routine.id);
  const add = () => {
    const created = {
      ...routine,
      id: uniqueSlug(slugify(routine.name), catalog.routines.map(r => r.id))
    };
    putItem('routines', created);
    navigate(`/be/protocollen/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-866" data-mp="8t96cx6ned7c">
      <PageHeader title={isNew ? 'Nieuw protocol' : routine.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!routine.name.trim()} className={btnPrimary} data-id="element-868" data-mp="11ipw1n2vvfzv">
              Protocol toevoegen
            </button> : <>
              <DeleteButton undoable blockedReason={deleteBlockedReason(usedBy, 'Kies daar eerst een ander protocol.')} onConfirm={() => {
        const restore = removeItem('routines', routine.id);
        navigate(back.to);
        undoToast(`“${routine.name || 'Naamloos protocol'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Protocol teruggezet'
        });
      }} data-id="element-869" data-mp="1dt8mhsivh9p7" />
              <ViewOnSite path="/routines" data-id="element-870" data-mp="21kmbbohten5f" />
            </>} data-id="element-867" data-mp="16ahh5i39stim" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-871" data-mp="26j7fu043nszo">
        <div className="space-y-6 lg:col-span-8" data-id="element-872" data-mp="26ks79gv4avtb">
          <Panel title="Over dit protocol" data-id="element-873" data-mp="2bik0ce7i0nz3">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-874" data-mp="2bfo0gkc3bp80">
              <Field label="Naam" className="sm:col-span-2" data-id="element-875" data-mp="2gg27bz1ygt4g">
                <input value={routine.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} data-id="element-876" data-mp="3d88s0qsentfo" />
              </Field>
              <Field label="Kernzin" hint="Eén zin op de protocolkaart." className="sm:col-span-2" data-id="element-877" data-mp="389xfg8aq74tr">
                <input value={routine.claim} onChange={e => set({
                claim: e.target.value
              })} className={inputCls} data-id="element-878" data-mp="3mx0falmitpk9" />
              </Field>
              <Field label="Voor wie" className="sm:col-span-2" data-id="element-879" data-mp="3hzqq9731nfwy">
                <input value={routine.forWho} onChange={e => set({
                forWho: e.target.value
              })} className={inputCls} data-id="element-880" data-mp="2uafnxl61pdgp" />
              </Field>
              <Field label="Het verhaal" hint="Waarom deze producten samen werken." className="sm:col-span-2" data-id="element-881" data-mp="2pce95cfnk0b1">
                <textarea value={routine.story} onChange={e => set({
                story: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-882" data-mp="2z8h7063h1cs6" />
              </Field>
              <Field label="Soort" data-id="element-883" data-mp="ilwunvmulso4">
                <select value={routine.level} onChange={e => {
                const level = oneOf(routineLevels, e.target.value);
                if (level) set({
                  level
                });
              }} className={inputCls} data-id="element-884" data-mp="eiiaqtvsgs5e">
                  {routineLevels.map(l => <option key={l} value={l} data-id="element-885" data-mp="c54ttit797l1">
                      {levelLabel[l]}
                    </option>)}
                </select>
              </Field>
              <Field label="Hoort bij behandeling" hint="Wordt aangeboden na die afspraak." data-id="element-886" data-mp="14tafc7s2v0lx">
                <select value={routine.treatmentSlug ?? ''} onChange={e => set({
                treatmentSlug: e.target.value || undefined
              })} className={inputCls} data-id="element-887" data-mp="19qajue9a3vax">
                  <option value="" data-id="element-888" data-mp="v0les7y2f75d">Geen</option>
                  {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-889" data-mp="zz661hvcagho">
                      {t.name}
                    </option>)}
                </select>
              </Field>
              <Field label="Gaat minimaal mee (maanden)" data-id="element-890" data-mp="1sj4skqgb6r6p">
                <NumberInput value={routine.lasts.minMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  minMonths: v
                }
              })} min={1} max={24} data-id="element-891" data-mp="22e23gnayt8pl" />
              </Field>
              <Field label="Gaat maximaal mee (maanden)" hint={routine.lasts.maxMonths < routine.lasts.minMonths ? 'Lager dan het minimum: de site toont dan alleen het minimum.' : undefined} data-id="element-892" data-mp="1oeauj0zb2iig">
                <NumberInput value={routine.lasts.maxMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  maxMonths: v
                }
              })} min={1} max={24} data-id="element-893" data-mp="1eng0vbjqcgmz" />
              </Field>
            </div>
          </Panel>

          <Panel title="Producten, in volgorde van gebruik" description="Reinigen → toner → serum → crème → SPF. Verplaats met de pijltjes." data-id="element-894" data-mp="10nop7hgorcc5">
            <ProductPicker value={routine.productIds} onChange={productIds => set({
            productIds
          })} products={catalog.products} ordered data-id="element-895" data-mp="37cz0vy00t92m" />
          </Panel>

          <Panel title="Wat er zonder extra kosten bij zit" description="Nooit een korting — wel begeleiding en papier." data-id="element-896" data-mp="3c9yxzjew71hl">
            <ContentEditor value={routine.included} onChange={v => set({
            included: asLines(v)
          })} fieldKey="included" data-id="element-897" data-mp="3h925daichjsq" />
          </Panel>

          <SeoPanel seo={routine.seo} onChange={seo => set({
          seo
        })} auto={routineSeoAuto(routine)} path={null} imageFallback="het sfeerbeeld" data-id="element-898" data-mp="2on0ml05mqjqj" />
        </div>

        <aside aria-label="Protocolinstellingen" className="space-y-6 lg:col-span-4" data-id="element-899" data-mp="2xmxdz9jelwpa">
          <Panel title="Prijs" data-id="element-900" data-mp="2xkt327n37xfi">
            <p className="font-display text-4xl font-light text-ink" data-id="element-901" data-mp="32e5ok8wgzo41">{euro(totals.price)}</p>
            <p className="mt-1 text-[13px] text-muted" data-id="element-902" data-mp="25vzki397q82r">Som van {totals.items.length} producten, automatisch.</p>
            <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-903" data-mp="29wtdhb1iid2i">
              {gift ? <>
                  Klant krijgt: <span className="text-ink" data-id="element-904" data-mp="13btxa1ln6ewq">{gift.title}</span>
                </> : 'Nog geen cadeautrede bereikt.'}
              {next && <>
                  <br data-id="element-905" data-mp="1893kdalk6ehe" />
                  Nog {euro(next.threshold - totals.price)} tot {next.phrase}.
                </>}
            </p>
          </Panel>

          <RoutineMarginPanel items={totals.items} data-id="element-906" data-mp="1m71x6bk8e2us" />

          <Panel title="Huidthema’s die dit dekt" data-id="element-907" data-mp="1habdzfd4qr55">
            <p className="text-[13px] leading-relaxed text-muted" data-id="element-908" data-mp="1h7paprx9uxsq">{covered.length ? covered.join(', ') : 'Nog geen — voeg producten toe.'}</p>
            {usedBy.length > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-909" data-mp="1r03f6sfqiiil">
                Aanbevolen bij: <span className="text-ink" data-id="element-910" data-mp="24ykuq2vhkopf">{usedBy.map(c => c.label).join(', ')}</span>
              </p>}
          </Panel>

          <Panel title="Sfeerbeeld" data-id="element-911" data-mp="20zluuwom3i4u">
            <ImageField value={routine.image} onChange={image => set({
            image
          })} fit="cover" aspect="aspect-[4/5]" data-id="element-912" data-mp="2jmb96k9vk8it" />
          </Panel>

          <Panel title="Wat mist er nog?" data-id="element-913" data-mp="2ojuxye4783p0">
            <CheckList checks={[...routineChecks(routine), ...routineSeoChecks(routine, catalog.routines)]} okText="Dit protocol is compleet" data-id="element-914" data-mp="2ois3xxbkxl4v" />
          </Panel>
        </aside>
      </div>
    </div>;
}